import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { SocialAnalyticsInsightsQuery } from '@/generated/graphql';
import type { AnalyticsRange } from '@/lib/social-analytics/analyticsFilters';
import { formatMetric } from '@/lib/social-analytics/formatMetric';
import { MIN_INSIGHT_POSTS } from '@/lib/social-analytics/insightThresholds';
import { mediaTypeKey } from '@/lib/social-analytics/mediaTypeKey';
import { rankFormats } from '@/lib/social-analytics/rankFormats';
import { resolveInsightCardState } from '@/lib/social-analytics/resolveInsightCardState';
import { InsightCardBody } from './InsightCardBody';

export type InsightCardProps = {
  insights: SocialAnalyticsInsightsQuery['socialAnalyticsInsights'] | undefined;
  range: AnalyticsRange;
  error: boolean;
  errorKey: string | null;
  onRetry: () => void;
};

export const FormatInsightsCard = ({ insights, range, error, errorKey, onRetry }: InsightCardProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('pt') ? 'pt-BR' : 'en-US';
  const state = resolveInsightCardState({ range, eligiblePosts: insights?.eligible_posts ?? null, error });
  const formats = rankFormats(insights?.formats ?? []);
  const best = Math.max(0, ...formats.filter((format) => format.sufficient).map((format) => format.median_views ?? 0));
  return (
    <Card className="border-border/70 shadow-none">
      <CardHeader className="border-b border-border/60 pb-4">
        <CardTitle className="text-base">{t('analytics.insights.formats.title')}</CardTitle>
        {(state === 'ready' || state === 'notEnough') && <p className="text-xs text-muted-foreground">{t('analytics.insights.subtitle', { count: insights?.eligible_posts ?? 0 })}</p>}
      </CardHeader>
      <CardContent className="p-4 sm:p-5">
        <InsightCardBody state={state} minPostAgeDays={insights?.min_post_age_days} errorKey={errorKey} onRetry={onRetry}>
          <ul className="space-y-3" aria-label={t('analytics.insights.formats.title')}>
            {formats.map((format) => (
              <li key={format.media_type} data-sufficient={format.sufficient} className={format.sufficient ? undefined : 'opacity-50'}>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium">{t(mediaTypeKey(format.media_type))}</span>
                  <span className="flex items-center gap-1 tabular-nums text-muted-foreground">
                    {formatMetric(format.median_views ?? null, locale)} · {t('analytics.insights.posts', { count: format.posts })}
                    {!format.sufficient && <AlertTriangle className="h-3 w-3" aria-label={t('analytics.insights.insufficientShort')} />}
                  </span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${format.sufficient && best > 0 ? Math.round(((format.median_views ?? 0) / best) * 100) : 8}%` }} />
                </div>
              </li>
            ))}
          </ul>
          {formats.some((format) => !format.sufficient) && (
            <p className="mt-3 text-xs text-muted-foreground">{t('analytics.insights.insufficient', { count: MIN_INSIGHT_POSTS })}</p>
          )}
        </InsightCardBody>
      </CardContent>
    </Card>
  );
};
